/** Account nav, overview booking helpers, profile completeness. */

import moment from 'moment';
import { countries } from 'countries-list';
import { getGuestDates } from 'day8-react/build/helpers/week';
import { isRedacted } from '../utils/redaction';
import { AMBASSADOR_PORTAL_HOME, isAmbassadorPortalRoute } from './ambassadorPortal';
import { getFeaturedData } from './week';

export const CHECK_IN_DAYS_BEFORE = 120;
export const CS_CONTACT_HREF = 'mailto:hello@theyachtweek.com';
export const EVENT_PROFILE_HINT_KEY = 'yw-profile-tabs-hint';
export const DOWNLOAD_APP_URL = 'https://www.theyachtweek.com/blog/2025/12/download-our-app';

export function pathToAccountActiveKey(pathname = '') {
  if (pathname.startsWith('/account/profile') || pathname.startsWith('/account/edit')) return 'profile';
  // Checked before /account/referrals so the portal keeps the sidebar on its own item.
  if (isAmbassadorPortalRoute(pathname)) return 'ambassador';
  if (pathname.startsWith('/account/referrals')) return 'refer';
  if (pathname.startsWith('/account/crew-finder')) return 'crews';
  if (pathname.startsWith('/account/change-password')) return 'password';
  if (pathname === '/account' || pathname.startsWith('/account#')) return 'overview';
  return null;
}

/** Mobile drawer / nav active item from URL (account routes + in-app plans). */
export function pathToMobileNavActiveKey(pathname = '') {
  if (pathname === '/plan' || pathname.startsWith('/plan/')) return 'plans';
  return pathToAccountActiveKey(pathname);
}

const ACCOUNT_AUTH_PATHS = /^\/account\/(login|signup|reset-password|recover-password)/;

export function isAccountAppRoute(pathname = '') {
  return /^\/account(\/|$)/.test(pathname) && !ACCOUNT_AUTH_PATHS.test(pathname);
}

/** Drawer highlight follows current URL; stale AccountMenu config only applies on /account routes. */
export function resolveMobileDrawerActiveKey(pathname = '', configActiveKey = null) {
  const fromPath = pathToMobileNavActiveKey(pathname);
  if (fromPath != null) return fromPath;
  if (isAccountAppRoute(pathname) && configActiveKey) return configActiveKey;
  return undefined;
}

export function getAccountCompletenessHint(account, pct, propHint) {
  if (propHint) return propHint;
  if (pct == null) return undefined;
  if (account?.incompleteProfile) return 'Add missing check-in details to finish your profile.';
  if (pct === 100) return 'Your profile is complete — see you on the water.';
  if (pct < 100) return 'Finish your profile for a smoother check-in.';
  return undefined;
}

// Ambassadors get the portal in place of Refer & earn. ROLE_AMBASSADOR is the gate, surfaced as
// account.isAmbassador by the API and enforced again by AmbassadorPortalLayout on every page.
const AMBASSADOR_PORTAL_TO = AMBASSADOR_PORTAL_HOME;

const ambassadorOrReferralItem = isAmbassador => (isAmbassador? { key: 'ambassador', label: 'Ambassador portal', to: AMBASSADOR_PORTAL_TO, arrow: '→', variant: 'primary' }: { key: 'refer', label: 'Refer & earn', to: '/account/referrals', arrow: '→', variant: 'primary' });

export function getAccountNavItems({ activeKey = 'overview', isAmbassador = false } = {}) {
  const items = [
    { key: 'overview', label: 'Account', to: '/account', arrow: '→', variant: 'primary' },
    { key: 'profile', label: 'Profile', to: '/account/profile', arrow: '→', variant: 'primary' },
    // In-app: PlanStart lists the guest's own plans, which the public
    // marketing page cannot. Matches the mobile drawer and the Saved tab.
    { key: 'plans', label: 'My plans', to: '/plan', arrow: '→', variant: 'primary' },
    { key: 'crews', label: 'Crew finder', to: '/account/crew-finder', arrow: '↗', variant: 'primary', newTab: true },
    ambassadorOrReferralItem(isAmbassador),
    { key: 'password', label: 'Change password', to: '/account/change-password', variant: 'sub' },
    { key: 'logout', label: 'Log out', action: 'logout', variant: 'sub' }
  ];
  return items.map(item => {
    const active = item.key === activeKey;
    const arrow = item.variant === 'sub' ? (active ? '→' : '') : item.arrow;
    return { ...item, active, arrow, hasArrow: !!arrow };
  });
}

/** Shared mobile drawer nav — same list on /browse, /account, /plan, etc. */
export function getAppDrawerNavItems({ activeKey, isAmbassador = false, showBrowseInfo = false } = {}) {
  const items = [
    { key: 'overview', label: 'Account', to: '/account', arrow: '→', variant: 'primary' },
    { key: 'profile', label: 'Profile', to: '/account/profile', arrow: '→', variant: 'primary' },
    { key: 'plans', label: 'My plans', to: '/plan', arrow: '→', variant: 'primary' },
    { key: 'crews', label: 'Crew finder', to: '/account/crew-finder', arrow: '↗', variant: 'primary' },
    ambassadorOrReferralItem(isAmbassador),
    {
      key: 'download',
      label: 'Download the app',
      href: DOWNLOAD_APP_URL,
      external: true,
      arrow: '↗',
      variant: 'sub'
    },
    ...(showBrowseInfo ? [{ key: 'info', label: 'Info', action: 'info', variant: 'sub' }] : []),
    { key: 'currency', type: 'currency', variant: 'sub' },
    { key: 'password', label: 'Change password', to: '/account/change-password', variant: 'sub' },
    { key: 'logout', label: 'Log out', action: 'logout', variant: 'sub' }
  ];
  return items.map(item => {
    const active = activeKey != null && item.key === activeKey;
    const arrow = item.variant === 'sub'? (active ? '→' : item.arrow || ''): item.arrow;
    return { ...item, active, arrow, hasArrow: !!arrow };
  });
}

/** @deprecated use getAppDrawerNavItems */
export function getMobileDrawerNavItems(opts) {
  return getAppDrawerNavItems(opts);
}

export const ACCOUNT_NAV_ARROW_CLASS = 'yw-account-nav__arrow';

export function getAccountNavItemClass(item, { drawer = false } = {}) {
  const placement = drawer ? 'yw-account-nav__item--drawer' : 'yw-account-nav__item--sidebar';
  const tier = item.variant === 'sub' ? 'yw-account-nav__item--sub' : 'yw-account-nav__item--primary';
  const active = item.active ? 'is-active' : '';
  return `yw-account-nav__item ${placement} ${tier} ${active}`.trim();
}

/** list when >2 entries on desktop; cards on mobile. Manual override wins. */
export function autoViewMode(count, isMobile) {
  if (isMobile) return 'cards';
  return count > 2 ? 'list' : 'cards';
}

export function getBookingImage(item) {
  const featuredImage = getFeaturedData(item?.booking?.week)?.image;
  if (featuredImage) {
    return featuredImage;
  }
  if (item?.image) {
    return {
      url: item.image.startsWith('http') ? item.image : `https://theyachtweek.com${item.image}`,
      alt: item.booking?.week?.destination?.name || 'Booking'
    };
  }
  if (item?.booking?.subject === 'cabin') {
    const cabinType = item.booking.bookedCabinWeeks?.[0]?.cabinWeek?.cabin?.type;
    return {
      url: `https://cdn.theyachtweek.com/assets/yachtlist/cabins/cabin-booking-${cabinType || 'double'}.jpg`,
      alt: `Booking ${item.booking.id}`
    };
  }
  return {
    url: 'https://wpassets.theyachtweek.com/wp-content/uploads/2019/11/monohull-image.png',
    alt: `Booking ${item?.booking?.id || ''}`
  };
}

export function formatBookingDates(week) {
  if (!week) return '';
  const { beginDate, endDate } = getGuestDates(week);
  return `${moment.utc(beginDate).format('D MMM')} – ${moment.utc(endDate).format('D MMM YYYY')}`;
}

/**
 * Derive overview check-in UI from account booking payload.
 * @returns {'opens'|'open'|'closing'|'closed'|'ended'|'done'|null}
 */
export function getCheckInState(item, { isPast } = {}) {
  if (isPast) return 'ended';
  if (!item?.booking?.week) return null;
  if (item.hasUserCheckedIn) return 'done';

  const begin = moment.utc(getGuestDates(item.booking.week).beginDate).startOf('day');
  const today = moment.utc().startOf('day');
  const daysUntilBegin = begin.diff(today, 'days');

  if (daysUntilBegin <= 0) return 'closed';

  if (!item.isCheckInOpen) return 'opens';

  if (daysUntilBegin <= 14) return 'closing';
  return 'open';
}

export function getCheckInOpensLabel(week) {
  if (!week) return '';
  const begin = moment.utc(getGuestDates(week).beginDate);
  return begin.clone().subtract(CHECK_IN_DAYS_BEFORE, 'days').format('D MMM');
}

export function getCheckInClosesLabel(week) {
  if (!week) return '';
  const begin = moment.utc(getGuestDates(week).beginDate).startOf('day');
  const today = moment.utc().startOf('day');
  const days = Math.max(0, begin.diff(today, 'days'));
  if (days > 14) {
    const weeks = Math.round(days / 7);
    return `closes in ${weeks} week${weeks === 1 ? '' : 's'}`;
  }
  return `closes in ${days} day${days === 1 ? '' : 's'}`;
}

export function normalizePlan(plan, userId) {
  const isOwner = plan?.booker?.id != null && userId != null && Number(plan.booker.id) === Number(userId);
  const boat = plan?.boatWeek?.boat;
  const standard = plan?.standardYachtWeek?.standardYacht;
  const imgPath = boat?.imageConnections?.[0]?.image?.path
    || standard?.imageConnections?.[0]?.image?.path
    || null;
  return {
    alphaId: plan.alphaId,
    route: plan.destination?.name || 'Plan',
    hull: boat?.boatType?.name || boat?.type || (plan.subject === 'cabin' ? 'Cabin' : 'Yacht'),
    model: boat?.name || standard?.name || standard?.package?.name || plan.subject || '—',
    creator: isOwner ? 'You' : (plan.booker?.firstName || plan.booker?.name || 'Guest'),
    isOwner,
    badge: isOwner ? 'Owner' : 'Invited',
    actionLabel: isOwner ? 'Edit plan' : 'View plan',
    link: `/plan/${plan.alphaId}`,
    status: plan.status,
    img: imgPath ? (imgPath.startsWith('http') ? imgPath : `https://www.theyachtweek.com${imgPath}`) : 'https://cdn.theyachtweek.com/assets/bo-2024/slider/yacht.png'
  };
}

const SEX_LABELS = { M: 'Male', F: 'Female' };

const MANDATORY_FIELDS = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'sex', label: 'Gender' },
  { key: 'birthDate', label: 'Birth date' },
  { key: 'address', label: 'Address' },
  { key: 'zip', label: 'Zip / post code' },
  { key: 'city', label: 'City' },
  { key: 'country', label: 'Country' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'birthCountry', label: 'Country of birth' },
  { key: 'placeOfBirth', label: 'Place of birth' },
  { key: 'passport', label: 'Passport number' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Mobile phone' },
  { key: 'nextOfKinFirstName', label: 'Emergency first name' },
  { key: 'nextOfKinLastName', label: 'Emergency last name' },
  { key: 'nextOfKinRelationship', label: 'Relationship' },
  { key: 'nextOfKinPhone', label: 'Emergency phone' }
];

export function isProfileFieldFilled(profile, key) {
  if (!profile) return false;
  if (key === 'birthDate') {
    if (profile.birthDate && typeof profile.birthDate === 'object') {
      const { day, month, year } = profile.birthDate;
      return !!(day && month && year);
    }
    return !!(profile.birthDate && String(profile.birthDate).trim());
  }
  const v = profile[key];
  // The GDPR purge writes a marker into text fields once a trip is old enough. It is a non-empty
  // string, so this used to count it as supplied: a returning guest's profile read as complete,
  // nothing prompted them to re-enter anything, and the check-in gates passed on values that mean
  // nothing. A field we redacted is a field we do not hold.
  if (isRedacted(v)) {
    return false;
  }
  return !!(v != null && String(v).trim());
}

function countryName(code) {
  if (!code) return '';
  return countries[String(code).toUpperCase()]?.name || code;
}

export function getCountryName(code) {
  return countryName(code);
}

function birthDateDisplay(birthDate) {
  if (!birthDate) return '';
  if (typeof birthDate === 'object' && birthDate.year) {
    const m = moment.utc({ year: birthDate.year, month: (birthDate.month || 1) - 1, day: birthDate.day || 1 });
    return m.isValid() ? m.format('D MMM YYYY') : '';
  }
  const m = moment.utc(birthDate);
  return m.isValid() ? m.format('D MMM YYYY') : '';
}

function viewRow(label, value) {
  const ok = !!(value != null && String(value).trim());
  return { label, value: ok ? String(value) : '', ok, missing: !ok, editHref: '/account/edit' };
}

export function getProfileViewSections(profile = {}, { crewView = false } = {}) {
  const docValue = profile.passport || profile.nationalId || '';
  const docLabel = profile.passport || !profile.nationalId ? 'Passport number' : 'National ID number';
  const manifestRows = [
    ...(crewView ? [] : [viewRow('Nationality', countryName(profile.nationality))]),
    viewRow('Country of birth', countryName(profile.birthCountry)),
    viewRow('Place of birth', profile.placeOfBirth),
    viewRow(docLabel, docValue),
    viewRow('Emergency first name', profile.nextOfKinFirstName),
    viewRow('Emergency last name', profile.nextOfKinLastName),
    viewRow('Emergency relationship', profile.nextOfKinRelationship),
    viewRow('Emergency phone', profile.nextOfKinPhone)
  ];
  const sections = [
    {
      key: 'personal',
      title: 'Personal details',
      rows: [
        viewRow('First name', profile.firstName),
        viewRow('Last name', profile.lastName),
        viewRow('Gender', SEX_LABELS[profile.sex] || profile.sex),
        viewRow('Birth date', birthDateDisplay(profile.birthDate)),
        viewRow('Address', profile.address),
        viewRow('Zip / post code', profile.zip),
        viewRow('City', profile.city),
        viewRow('Country', countryName(profile.country))
      ]
    },
    {
      key: 'manifest',
      title: 'Crew list manifest',
      note: 'Required by the charter company before check-in.',
      rows: manifestRows
    },
    {
      key: 'contact',
      title: 'Contact',
      rows: [viewRow('Email', profile.email), viewRow('Mobile phone', profile.phone)]
    }
  ];
  return sections.map(sec => {
    const nMissing = sec.rows.filter(r => r.missing).length;
    return {
      ...sec,
      hasMissing: nMissing > 0,
      missingBadge: nMissing === 0 ? '' : (nMissing === 1 ? '1 missing' : `${nMissing} missing`),
      hasNote: !!sec.note
    };
  });
}

export function getMissingCheckInLabels(profile) {
  return MANDATORY_FIELDS.filter(f => !isProfileFieldFilled(profile, f.key)).map(f => f.label.toLowerCase());
}

export function formatMissingSummary(labels) {
  if (!labels.length) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

export function getMandatoryCompletenessPct(profile) {
  if (!profile) return 0;
  const filled = MANDATORY_FIELDS.filter(f => isProfileFieldFilled(profile, f.key)).length;
  return Math.round((filled / MANDATORY_FIELDS.length) * 100);
}

const EVENT_FIELD_ITEMS = [
  {
    key: 'languages',
    label: 'languages',
    isFilled(profile) {
      return normalizeEventProfile(profile?.eventProfile).languages.length > 0;
    }
  },
  {
    key: 'bio',
    label: 'bio',
    isFilled(profile) {
      return !!(normalizeEventProfile(profile?.eventProfile).bio || '').trim();
    }
  },
  {
    key: 'skills',
    label: 'skills',
    isFilled(profile) {
      return normalizeEventProfile(profile?.eventProfile).skills.length > 0;
    }
  },
  {
    key: 'socials',
    label: 'a social account',
    isFilled(profile) {
      const socials = normalizeEventProfile(profile?.eventProfile).socials;
      return EVENT_SOCIAL_FIELDS.some(f => (socials[f.key] || '').trim());
    }
  }
];

const PHOTO_SLOT_ITEMS = [
  {
    key: 'main',
    label: 'main profile photo',
    isFilled(profile) {
      return !!(profile?.profilePicture || '').trim();
    }
  },
  {
    key: 'photo0',
    label: 'photo 2',
    isFilled(profile) {
      return !!(normalizeEventProfile(profile?.eventProfile).photos[0] || '').trim();
    }
  },
  {
    key: 'photo1',
    label: 'photo 3',
    isFilled(profile) {
      return !!(normalizeEventProfile(profile?.eventProfile).photos[1] || '').trim();
    }
  },
  {
    key: 'photo2',
    label: 'photo 4',
    isFilled(profile) {
      return !!(normalizeEventProfile(profile?.eventProfile).photos[2] || '').trim();
    }
  }
];

/** Weights for overall profile % (must sum to 100). */
export const PROFILE_COMPLETENESS_WEIGHTS = {
  mandatory: 70,
  eventFields: 20,
  photos: 10
};

function bucketPct(items, profile) {
  if (!items.length) return 0;
  const filled = items.filter(item => item.isFilled(profile)).length;
  return Math.round((filled / items.length) * 100);
}

export function getEventFieldsCompletenessPct(profile) {
  return bucketPct(EVENT_FIELD_ITEMS, profile);
}

export function getPhotosCompletenessPct(profile) {
  return bucketPct(PHOTO_SLOT_ITEMS, profile);
}

/** @deprecated use getEventFieldsCompletenessPct — kept for tests */
export function getEventCompletenessPct(profile) {
  return getEventFieldsCompletenessPct(profile);
}

/** Same as mandatory % — shared with AccountController profileCompleteness. */
export function getProfileCompletenessPct(profile) {
  return getMandatoryCompletenessPct(profile);
}

export function getMissingEventProfileLabels(profile) {
  return [
    ...EVENT_FIELD_ITEMS.filter(item => !item.isFilled(profile)).map(item => item.label),
    ...PHOTO_SLOT_ITEMS.filter(item => !item.isFilled(profile)).map(item => item.label)
  ];
}

export function isEventProfileHintDismissed() {
  try {
    return localStorage.getItem(EVENT_PROFILE_HINT_KEY) === 'dismissed';
  } catch {
    return false;
  }
}

export function dismissEventProfileHint() {
  try {
    localStorage.setItem(EVENT_PROFILE_HINT_KEY, 'dismissed');
  } catch {
    // ignore
  }
}

export function getLinkedAccountState(user) {
  const providers = [
    { key: 'google', name: 'Google', linked: !!user?.googleUid, canLink: true },
    { key: 'facebook', name: 'Facebook', linked: !!user?.fbUid, canLink: true },
    { key: 'apple', name: 'Apple', linked: !!user?.appleUid, canLink: true }
  ].map(p => ({
    ...p,
    status: p.linked ? 'Linked' : 'Not linked'
  }));
  return {
    title: providers.some(p => p.linked) ? 'Linked accounts' : 'Link your account',
    providers
  };
}

export const EVENT_SKILL_SUGGESTIONS = [
  'Sunrise yoga', 'Free diving', 'Acoustic guitar', 'DJ & playlists', 'Photography',
  'Videography', 'Drone pilot', 'Mixology', 'Cooking', 'First aid certified',
  'Fishing', 'Navigation', 'Water sports instructor', 'Massage therapist'
];

export const EVENT_SOCIAL_FIELDS = [
  { key: 'instagram', label: 'Instagram', placeholder: 'Instagram handle' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'TikTok handle' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'LinkedIn profile' },
  { key: 'facebook', label: 'Facebook', placeholder: 'Facebook profile' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: 'WhatsApp number' },
  { key: 'snapchat', label: 'Snapchat', placeholder: 'Snapchat handle' },
  { key: 'x', label: 'X', placeholder: 'X handle' },
  { key: 'threads', label: 'Threads', placeholder: 'Threads handle' }
];

export const EVENT_VISIBILITY_FIELDS = [
  { key: 'nationality', label: 'Nationality' },
  { key: 'languages', label: 'Languages' },
  { key: 'bio', label: 'Bio' },
  { key: 'socials', label: 'Social channels' },
  { key: 'skills', label: 'Skills' },
  { key: 'weeksSailed', label: 'Weeks sailed' }
];

const EMPTY_SOCIALS = EVENT_SOCIAL_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

export function defaultEventProfile() {
  return {
    bio: '',
    languages: [],
    skills: [],
    socials: { ...EMPTY_SOCIALS },
    visibility: EVENT_VISIBILITY_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: true }), {}),
    photos: []
  };
}

const LANGUAGE_LABEL_BY_VALUE = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  hr: 'Croatian',
  el: 'Greek',
  nl: 'Dutch',
  pt: 'Portuguese',
  sv: 'Swedish',
  no: 'Norwegian',
  english: 'English',
  spanish: 'Spanish',
  french: 'French',
  german: 'German',
  italian: 'Italian',
  croatian: 'Croatian',
  greek: 'Greek',
  dutch: 'Dutch',
  portuguese: 'Portuguese',
  swedish: 'Swedish',
  norwegian: 'Norwegian',
  other: 'Other'
};

export function normalizeEventLanguageLabel(value) {
  const raw = (value || '').trim();
  if (!raw) return '';
  if (LANGUAGE_LABEL_BY_VALUE[raw.toLowerCase()]) {
    return LANGUAGE_LABEL_BY_VALUE[raw.toLowerCase()];
  }
  return raw;
}

export function normalizeEventProfile(stored) {
  const base = defaultEventProfile();
  if (!stored) return base;
  const photos = Array.isArray(stored.photos) ? [...stored.photos] : [];
  while (photos.length < 3) photos.push('');
  const languages = Array.isArray(stored.languages)? stored.languages.map(normalizeEventLanguageLabel).filter(Boolean): [];
  return {
    ...base,
    ...stored,
    socials: { ...base.socials, ...(stored.socials || {}) },
    visibility: { ...base.visibility, ...(stored.visibility || {}) },
    languages: [...new Set(languages)],
    skills: Array.isArray(stored.skills) ? stored.skills : [],
    photos: photos.slice(0, 3)
  };
}

/** Strip event-profile fields hidden from crew / public profile views. */
export function applyEventProfileVisibility(stored) {
  const event = normalizeEventProfile(stored);
  const { visibility } = event;
  const visible = { ...event };

  if (!visibility.bio) visible.bio = '';
  if (!visibility.languages) visible.languages = [];
  if (!visibility.skills) visible.skills = [];
  if (!visibility.socials) {
    visible.socials = { ...EMPTY_SOCIALS };
  }
  delete visible.visibility;

  return visible;
}

/** Social rows with a handle, for profile view chips. */
export function stripSocialHandle(value) {
  return (value || '').replace(/^@+/, '').trim();
}

/** Parse stored social value into href + link label for profile view. */
export function formatEventSocialLink(network, raw) {
  const value = (raw || '').trim();
  if (!value) return { href: '', linkText: '' };

  const field = EVENT_SOCIAL_FIELDS.find(f => f.key === network);
  const label = field?.label || network;
  const isUrl = /^https?:\/\//i.test(value);
  let handle = '';

  if (isUrl) {
    try {
      const parts = new URL(value).pathname.split('/').filter(Boolean);
      switch (network) {
      case 'instagram':
        handle = parts[0] && !['p', 'reel', 'reels', 'stories', 'explore'].includes(parts[0]) ? parts[0] : '';
        break;
      case 'tiktok':
        handle = stripSocialHandle(parts.find(p => p.startsWith('@')) || parts[0] || '');
        break;
      case 'linkedin':
        handle = parts[0] === 'in' ? (parts[1] || '') : (parts[parts.length - 1] || '');
        break;
      case 'facebook':
        handle = parts[0] && parts[0] !== 'profile.php' ? parts[0] : '';
        break;
      case 'snapchat':
        handle = parts[0] === 'add' ? (parts[1] || '') : (parts[0] || '');
        break;
      case 'x':
        handle = stripSocialHandle(parts[0] || '');
        break;
      case 'threads':
        handle = stripSocialHandle(parts[0] || '');
        break;
      case 'whatsapp':
        handle = value.replace(/\D/g, '');
        break;
      default:
        handle = '';
      }
    } catch {
      handle = '';
    }
  } else if (network === 'whatsapp') {
    handle = value.replace(/\D/g, '');
  } else {
    handle = stripSocialHandle(value);
  }

  handle = stripSocialHandle(handle);
  const href = buildEventSocialHref(network, handle, isUrl ? value : '');
  let linkText = label;

  if (network === 'whatsapp' && handle) {
    linkText = value.startsWith('+') ? value : `+${handle}`;
  } else if (handle) {
    linkText = `${label} · @${handle}`;
  }

  return { href: href || (isUrl ? value : ''), linkText };
}

function buildEventSocialHref(network, handle, fallbackUrl) {
  if (!handle) return fallbackUrl || '';
  switch (network) {
  case 'instagram':
    return `https://instagram.com/${handle}`;
  case 'tiktok':
    return `https://www.tiktok.com/@${handle}`;
  case 'linkedin':
    return fallbackUrl || `https://www.linkedin.com/in/${handle}`;
  case 'facebook':
    return fallbackUrl || `https://www.facebook.com/${handle}`;
  case 'whatsapp':
    return `https://wa.me/${handle.replace(/\D/g, '')}`;
  case 'snapchat':
    return `https://www.snapchat.com/add/${handle}`;
  case 'x':
    return `https://x.com/${handle}`;
  case 'threads':
    return `https://www.threads.net/@${handle}`;
  default:
    return fallbackUrl || '';
  }
}

export function getFilledEventSocials(eventProfile) {
  const event = normalizeEventProfile(eventProfile);
  return EVENT_SOCIAL_FIELDS
    .map(f => {
      const raw = (event.socials[f.key] || '').trim();
      if (!raw) return null;
      const { href, linkText } = formatEventSocialLink(f.key, raw);
      return { ...f, raw, href, linkText };
    })
    .filter(Boolean);
}
